import { useEffect, useState, useMemo } from "react";
import { BellRing, CheckCircle2, Filter } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useFideicomiso } from "../lib/fideicomiso";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const SEMAFORO_BADGE = {
    verde: "badge-exito",
    gold: "badge-gold",
    rojo: "badge-error",
    negro: "badge-error",
};

const TIPOS = ["todos", "kyc", "avaluo", "fiscal", "contractual", "honorario", "covenant", "otro"];

export default function Obligaciones() {
    const { selected } = useFideicomiso();
    const [rows, setRows] = useState([]);
    const [tipoFilter, setTipoFilter] = useState("todos");
    const [showCumplidas, setShowCumplidas] = useState(false);
    const [scope, setScope] = useState("fideicomiso");  // fideicomiso | global

    const load = async () => {
        const q = new URLSearchParams();
        if (scope === "fideicomiso" && selected?.id) q.set("fideicomiso_id", selected.id);
        if (tipoFilter !== "todos") q.set("tipo", tipoFilter);
        const { data } = await api.get(`/obligaciones?${q}`);
        setRows(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [selected?.id, tipoFilter, scope]);

    const cumplir = async (id) => {
        const notas = prompt("Notas de cumplimiento (opcional):");
        try {
            await api.post(`/obligaciones/${id}/cumplir`, { notas: notas || null });
            toast.success("Marcada como cumplida");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    };

    const filtered = useMemo(() => {
        return rows.filter((r) => showCumplidas || r.estado !== "cumplida");
    }, [rows, showCumplidas]);

    const kpi = useMemo(() => {
        return {
            vencidas: filtered.filter((r) => r.semaforo === "negro").length,
            criticas: filtered.filter((r) => r.semaforo === "rojo").length,
            proximas: filtered.filter((r) => r.semaforo === "gold").length,
            total: filtered.length,
        };
    }, [filtered]);

    if (scope === "fideicomiso" && !selected) {
        return <div className="p-10 text-muted-foreground text-sm">Selecciona un fideicomiso o cambia a vista global.</div>;
    }

    return (
        <div className="p-8 lg:p-10 max-w-[1500px] space-y-6" data-testid="obligaciones-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Núcleo · {scope === "fideicomiso" ? selected?.numero_interno : "Vista global"}
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <BellRing className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Obligaciones y alertas
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Feed unificado: KYC + fiscal + honorarios + contractuales
                    </p>
                </div>
                <div className="flex gap-2 items-baseline">
                    <Button size="sm" variant={scope === "fideicomiso" ? "default" : "outline"}
                        onClick={() => setScope("fideicomiso")}
                        data-testid="scope-fideicomiso-btn">Fideicomiso</Button>
                    <Button size="sm" variant={scope === "global" ? "default" : "outline"}
                        onClick={() => setScope("global")}
                        data-testid="scope-global-btn">Global</Button>
                </div>
            </div>

            <div className="grid md:grid-cols-4 gap-3">
                <div className="kpi-card">
                    <div className="text-[10px] uppercase text-muted-foreground">Vencidas</div>
                    <div className={`kpi-value ${kpi.vencidas > 0 ? "text-[color:var(--brand-coral)]" : ""}`}>{kpi.vencidas}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase text-muted-foreground">Críticas (&lt;15d)</div>
                    <div className={`kpi-value ${kpi.criticas > 0 ? "text-[color:var(--brand-coral)]" : ""}`}>{kpi.criticas}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase text-muted-foreground">Próximas (30d)</div>
                    <div className="kpi-value">{kpi.proximas}</div>
                </div>
                <div className="kpi-card">
                    <div className="text-[10px] uppercase text-muted-foreground">Total mostradas</div>
                    <div className="kpi-value">{kpi.total}</div>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
                <Filter className="h-4 w-4 text-muted-foreground" />
                {TIPOS.map((t) => (
                    <Button key={t} size="sm" variant={tipoFilter === t ? "default" : "outline"}
                        onClick={() => setTipoFilter(t)}
                        data-testid={`filter-${t}-btn`}>{t}</Button>
                ))}
                <label className="ml-4 text-xs flex items-center gap-1">
                    <input type="checkbox" checked={showCumplidas}
                        onChange={(e) => setShowCumplidas(e.target.checked)} />
                    mostrar cumplidas
                </label>
            </div>

            <div className="rounded-md border border-border bg-card overflow-hidden">
                <table className="trf-table">
                    <thead><tr>
                        <th></th><th>Tipo</th><th>Título</th><th>Fecha límite</th>
                        <th>Responsable</th><th>Estado</th><th></th>
                    </tr></thead>
                    <tbody>
                        {filtered.map((o) => (
                            <tr key={o.id} data-testid={`obligacion-row-${o.id}`}>
                                <td><span className={SEMAFORO_BADGE[o.semaforo] || "badge-gold"}>{o.semaforo}</span></td>
                                <td><span className="badge-gold text-[10px]">{o.tipo}</span></td>
                                <td className="text-xs">{o.titulo}
                                    {o.descripcion && (
                                        <div className="text-[10px] text-muted-foreground">{o.descripcion}</div>
                                    )}
                                </td>
                                <td className="tabular">{o.fecha_limite}</td>
                                <td className="text-xs">{(o.responsable_rol || []).join(", ")}</td>
                                <td><span className={
                                    o.estado === "cumplida" ? "badge-exito"
                                        : o.estado === "vencida" ? "badge-error" : "badge-gold"
                                }>{o.estado}</span></td>
                                <td>
                                    {o.estado !== "cumplida" && (
                                        <Button size="sm" variant="ghost"
                                            onClick={() => cumplir(o.id)}
                                            data-testid={`cumplir-obligacion-${o.id}`}>
                                            <CheckCircle2 className="h-3 w-3 mr-1" /> Cumplir
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Sin obligaciones.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
